import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  makeStyles
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import SydButton from '../../../../../commonDesign/Button'
import { useModifyQueryBlueprint } from '../../../../../../api/denali'
import { decipherError } from '../../../../../../utils/decipherError'

const MONO = "'SF Mono', 'Fira Code', Menlo, Consolas, monospace"

const useStyles = makeStyles(() => ({
  paper: {
    minWidth: '520px',
    maxWidth: '720px'
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    paddingRight: '8px'
  },
  titleText: {
    flex: 1,
    fontSize: '15px',
    fontWeight: 600
  },
  contextChip: {
    fontSize: '11px',
    fontFamily: MONO,
    color: 'var(--color-neutral-500)',
    padding: '2px 6px',
    backgroundColor: 'var(--color-neutral-100)',
    borderRadius: '4px'
  },
  body: {
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
    paddingTop: '8px'
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  },
  label: {
    fontSize: '11px',
    fontWeight: 700,
    color: 'var(--color-neutral-700)',
    textTransform: 'uppercase',
    letterSpacing: '0.04em'
  },
  hint: {
    fontSize: '11px',
    color: 'var(--color-neutral-500)'
  },
  input: {
    padding: '8px 10px',
    border: '1px solid var(--color-neutral-300)',
    borderRadius: 'var(--border-radius-sm)',
    fontSize: 'var(--text-size-sm)',
    fontFamily: 'inherit',
    outline: 'none',
    backgroundColor: 'white',
    color: 'var(--color-neutral-900)',
    '&:focus': {
      borderColor: 'var(--color-primary-400)',
      boxShadow: '0 0 0 2px var(--color-primary-100)'
    }
  },
  inputReadonly: {
    backgroundColor: 'var(--color-neutral-050)',
    color: 'var(--color-neutral-600)',
    fontFamily: MONO,
    cursor: 'default'
  },
  textarea: {
    padding: '8px 10px',
    border: '1px solid var(--color-neutral-300)',
    borderRadius: 'var(--border-radius-sm)',
    fontSize: 'var(--text-size-sm)',
    fontFamily: 'inherit',
    minHeight: '88px',
    resize: 'vertical',
    outline: 'none',
    backgroundColor: 'white',
    color: 'var(--color-neutral-900)',
    '&:focus': {
      borderColor: 'var(--color-primary-400)',
      boxShadow: '0 0 0 2px var(--color-primary-100)'
    }
  },
  enabledToggle: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 12px',
    border: '1px solid var(--color-neutral-300)',
    borderRadius: 'var(--border-radius-sm)',
    backgroundColor: 'white',
    fontSize: 'var(--text-size-sm)',
    cursor: 'pointer',
    userSelect: 'none',
    color: 'var(--color-neutral-700)',
    width: 'fit-content',
    '&:hover': {
      borderColor: 'var(--color-primary-400)'
    }
  },
  enabledToggleOn: {
    backgroundColor: 'var(--color-success-050, #f0fdf4)',
    borderColor: 'var(--color-success-200, #bbf7d0)',
    color: 'var(--color-success-700, #15803d)'
  },
  enabledIndicator: {
    width: '9px',
    height: '9px',
    borderRadius: '50%',
    backgroundColor: 'var(--color-neutral-400)'
  },
  enabledIndicatorOn: {
    backgroundColor: 'var(--color-success-500, #22c55e)'
  },
  errorBanner: {
    padding: '8px 10px',
    backgroundColor: 'var(--color-danger-050, #fef2f2)',
    border: '1px solid var(--color-danger-200, #fecaca)',
    borderRadius: 'var(--border-radius-sm)',
    fontSize: '12px',
    color: 'var(--color-danger-800, #991b1b)'
  }
}))

function BlueprintMetadataModal ({ open, onClose, blueprint, onSaved }) {
  const classes = useStyles()
  const { mutateAsync: modifyBlueprint, isLoading: saving } = useModifyQueryBlueprint()

  const [form, setForm] = useState(() => ({
    name: blueprint?.name || '',
    description: blueprint?.description || '',
    enabled: !!blueprint?.enabled
  }))
  const [error, setError] = useState(null)

  // Read latest blueprint via ref so the open-sync effect doesn't re-fire on parent
  // refetches and clobber the user's edits while the modal is open.
  const blueprintRef = useRef(blueprint)
  useEffect(() => { blueprintRef.current = blueprint })

  useEffect(() => {
    if (open) {
      const bp = blueprintRef.current
      setForm({
        name: bp?.name || '',
        description: bp?.description || '',
        enabled: !!bp?.enabled
      })
      setError(null)
    }
  }, [open])

  const dirty = useMemo(() => {
    if (!blueprint) return false
    return (form.name || '') !== (blueprint.name || '') ||
      (form.description || '') !== (blueprint.description || '') ||
      Boolean(form.enabled) !== Boolean(blueprint.enabled)
  }, [form, blueprint])

  const handleSave = useCallback(async () => {
    const trimmedName = form.name.trim()
    if (!trimmedName) {
      setError('Name cannot be empty.')
      return
    }
    try {
      setError(null)
      await modifyBlueprint({
        blueprintId: blueprint.blueprintId,
        name: trimmedName,
        description: form.description.trim() || null,
        enabled: form.enabled
      })
      if (onSaved) await onSaved()
      onClose()
    } catch (err) {
      setError(decipherError(err) || 'Could not save metadata.')
    }
  }, [form, blueprint, modifyBlueprint, onSaved, onClose])

  const handleClose = useCallback(() => {
    if (saving) return
    if (dirty) {
      const ok = window.confirm('Discard unsaved metadata changes?')
      if (!ok) return
    }
    onClose()
  }, [dirty, saving, onClose])

  if (!blueprint) return null

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      classes={{ paper: classes.paper }}
    >
      <DialogTitle disableTypography>
        <div className={classes.title}>
          <span className={classes.titleText}>Edit blueprint metadata</span>
          <span className={classes.contextChip}>{blueprint.code}</span>
          <IconButton size='small' onClick={handleClose} aria-label='Close'>
            <CloseIcon fontSize='small' />
          </IconButton>
        </div>
      </DialogTitle>
      <DialogContent dividers>
        <div className={classes.body}>
          {error && <div className={classes.errorBanner}>{error}</div>}

          <div className={classes.field}>
            <label className={classes.label}>Code</label>
            <input
              className={`${classes.input} ${classes.inputReadonly}`}
              value={blueprint.code || ''}
              readOnly
            />
            <span className={classes.hint}>Code is immutable after creation.</span>
          </div>

          <div className={classes.field}>
            <label className={classes.label}>Name</label>
            <input
              className={classes.input}
              value={form.name}
              onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
              maxLength={128}
              autoFocus
              placeholder='Display name'
            />
            <span className={classes.hint}>Shown to users picking blueprints.</span>
          </div>

          <div className={classes.field}>
            <label className={classes.label}>Description</label>
            <textarea
              className={classes.textarea}
              value={form.description}
              onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
              placeholder='What this blueprint is for, who uses it, anything else worth knowing.'
              rows={4}
            />
          </div>

          <div className={classes.field}>
            <label className={classes.label}>Status</label>
            <button
              type='button'
              className={`${classes.enabledToggle} ${form.enabled ? classes.enabledToggleOn : ''}`}
              onClick={() => setForm(prev => ({ ...prev, enabled: !prev.enabled }))}
              title={form.enabled ? 'Click to disable this blueprint' : 'Click to enable this blueprint'}
            >
              <span className={`${classes.enabledIndicator} ${form.enabled ? classes.enabledIndicatorOn : ''}`} />
              {form.enabled ? 'Enabled' : 'Disabled'}
            </button>
            <span className={classes.hint}>
              Disabled blueprints stay editable but are hidden from end-user pickers.
            </span>
          </div>
        </div>
      </DialogContent>
      <DialogActions>
        <SydButton size='sm' variant='ghost' onClick={handleClose} disabled={saving}>
          Cancel
        </SydButton>
        <SydButton
          size='sm'
          variant='primary'
          onClick={handleSave}
          disabled={!dirty || saving || !form.name.trim()}
        >
          {saving ? 'Saving\u2026' : 'Save changes'}
        </SydButton>
      </DialogActions>
    </Dialog>
  )
}

BlueprintMetadataModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  blueprint: PropTypes.shape({
    blueprintId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    code: PropTypes.string,
    name: PropTypes.string,
    description: PropTypes.string,
    enabled: PropTypes.bool
  }),
  onSaved: PropTypes.func
}

export default BlueprintMetadataModal
